// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PopupContextProvider from "@gcg/App/Components/XPopup/XContext";
import XPopup from "@gcg/App/Components/XPopup/XPopup";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@gcg/Overrides/Components/ProductAddToListLink";
import ProductDescription, { ProductDescriptionStyles } from "@gcg/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@gcg/Overrides/Components/ProductPartNumbers";
import Close from "@gcg/XDL/Icons/Close";
import MiniMenu from "@gcg/XDL/Icons/MiniMenu";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Tooltip from "@insite/mobius/Tooltip";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {
    updateProductInfo,
    removeProductIdToCompare,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    recommendationId?: number;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    view: string;
    hideAvailability?: boolean;
    showElipsesOn?: string;
    customActions?: boolean;
    customActionsTemplate?: (product: ProductModel) => React.ReactNode;
    showBadges?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: InjectableCss;
    qtyGridItem?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productImageInfoWrapperStyles?: InjectableCss;
    productImageWrapperStyles?: InjectableCss;
    productInfoWrapperStyles?: InjectableCss;
    uomGridItemStyle?: InjectableCss;
    productAvailabilityWrapper?: InjectableCss;
    topWrapper?: InjectableCss;
    chooseOptionsClickableStyles?: ClickablePresentationProps;
    badgesContainerStyle?: InjectableCss;
    partNumbersStyles?: InjectableCss;
    partNumbersStylesComponent?: ProductPartNumbersStyles;
    moreNumbersMenu?: InjectableCss;
    customActionStyle?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productDescriptionStyles: {
        productDetailLink: {
            color: "text.main",
            typographyProps: {
                size: 14,
                weight: "normal",
                lineHeight: "20px",
            },
            css: css`
                margin-bottom: 5px;
                min-height: 60px;
                display: flex;
                align-items: flex-start;
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: "normal",
            lineHeight: "20px",
            css: css`
                margin-bottom: 5px;
                min-height: 60px;
                display: flex;
                align-items: flex-start;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            margin-bottom: 22px;
            * {
                font-size: 24px;
                line-height: 26px;
                font-weight: normal;
                color: ${getColor("text.main")};
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css``,
        },
    },
    addToCartContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    qtyGridItem: {
        css: css`
            width: 50px;
            max-width: 50px;
            flex-basis: 50px;
            height: 32px;
            margin-right: 10px;

            input {
                height: 32px !important;
            }
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                input {
                    text-align: center;
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        css: css`
            width: auto;
            flex: 1;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: {
                size: 14,
                weight: "normal",
                lineHeight: "16px",
                color: "text.main",
                css: css`
                    letter-spacing: 0.3px;
                    color: ${getColor("text.main")} !important;
                `,
            },
        },
        wrapper: {
            css: css`
                margin-top: 20px;
            `,
        },
    },
    productImageInfoWrapperStyles: {
        css: css`
            position: relative;

            .product-attr-label,
            .product-attr-value {
                font-size: 14px;
                line-height: 18px;
                font-weight: normal;
            }

            .product-attr-label {
                font-weight: bold;
            }
        `,
    },
    productImageWrapperStyles: {
        css: css`
            width: 220px;
            height: 220px;
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 18px;
            max-width: 100%;

            img {
                max-width: 100%;
                max-height: 220px;
                overflow: hidden;
            }
        `,
    },
    productInfoWrapperStyles: {
        css: css``,
    },
    uomGridItemStyle: {
        css: css`
            width: 50px;
            max-width: 50px;
            flex-basis: 50px;
            height: 32px;
            margin-right: 10px;
            div {
                margin-top: 0 !important;
            }
            select {
                height: 32px !important;
                padding-right: 13px !important;
            }
            select + span {
                padding-right: 0 !important;
                width: 22px !important;
                right: 0 !important;
                top: 0 !important;
                height: 32px !important;
            }
        `,
    },
    productAvailabilityWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 20px;
        `,
    },
    topWrapper: {
        css: css`
            padding: 30px 20px 20px 20px;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            background: ${getColor("common.white")};

            @media screen and (max-width: 410px) {
                padding: 30px 10px 20px 10px;
            }
        `,
    },
    chooseOptionsClickableStyles: {
        css: css`
            display: block;
            width: 100%;
            font-size: 18px;
            letter-spacing: 0.5px;
            margin-bottom: 43px;

            &:focus {
                outline-width: 0;
            }
            > button {
                width: 100%;
            }
        `,
    },
    badgesContainerStyle: {
        css: css`
            position: absolute;
            top: -25px;
            right: -15px;
            display: flex;

            &.hascustomActionsTemplate {
                right: 5px;
            }
            button {
                padding: 0;
                width: 26px;
                height: 26px;
            }
            img {
                max-width: 26px;
                max-height: 26px;
            }
        `,
    },
    partNumbersStyles: {
        css: css`
            min-height: 31px;
            display: flex;
            align-items: center;
            svg {
                width: 20px;
                height: 24px;
            }
            button {
                padding: 0;
            }
        `,
    },
    partNumbersStylesComponent: {
        container: {
            gap: 0,
        },
    },
};

export const product2by3ProductStyles: ProductCarouselProductStyles = {
    productDescriptionStyles: {
        productDetailLink: {
            color: "text.main",
            typographyProps: {
                size: 14,
                weight: "normal",
                lineHeight: "17px",
            },
            css: css`
                margin-bottom: 7px;
                min-height: 51px;
                display: flex;
                align-items: flex-start;
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: "normal",
            lineHeight: "17px",
            css: css`
                margin-bottom: 7px;
                min-height: 51px;
                display: flex;
                align-items: flex-start;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            margin-bottom: 8px;
            * {
                font-size: 20px;
                line-height: 22px;
                font-weight: normal;
                color: ${getColor("text.main")};
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css``,
        },
    },
    addToCartContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    qtyGridItem: {
        css: css`
            width: 65px;
            margin-right: 15px;
            @media screen and (max-width: 410px) {
                width: 40px;
                margin-right: 10px;
            }
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                input {
                    text-align: center;
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        css: css`
            &.has-multiple-uom {
                width: calc(100% - 160px);
                @media screen and (max-width: 410px) {
                    width: calc(100% - 110px);
                }
            }
            &:not(.has-multiple-uom) {
                width: calc(100% - 80px);

                @media screen and (max-width: 410px) {
                    width: calc(100% - 50px);
                }
            }
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: {
                size: 14,
                weight: "normal",
                lineHeight: "16px",
                color: "text.main",
                css: css`
                    letter-spacing: 0.3px;
                    color: ${getColor("text.main")} !important;
                `,
            },
        },
        wrapper: {
            css: css`
                margin-top: 20px;
            `,
        },
    },
    productImageInfoWrapperStyles: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 8px;
        `,
    },
    productImageWrapperStyles: {
        css: css`
            width: 101px;
            height: 101px;
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            max-width: 100%;

            img {
                max-width: 101px;
                max-height: 101px;
            }
        `,
    },
    productInfoWrapperStyles: {
        css: css`
            width: calc(100% - 120px);
        `,
    },
    uomGridItemStyle: {
        css: css`
            width: 65px;
            margin-right: 15px;
            div {
                margin-top: 0 !important;
            }
            select {
                padding-right: 16px !important;
            }
            select + span {
                padding-right: 0 !important;
                width: 25px !important;
                right: 0 !important;
            }

            @media screen and (max-width: 410px) {
                width: 50px;
                margin-right: 10px;
            }
        `,
    },
    productAvailabilityWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 7px;
        `,
    },
    topWrapper: {
        css: css`
            width: 100%;
            padding: 17px 19px 9px 20px;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            @media screen and (max-width: 410px) {
                padding-left: 0;
                padding-right: 0;
            }
        `,
    },
    chooseOptionsClickableStyles: {
        css: css`
            display: block;
            width: 100%;
            font-size: 18px;
            letter-spacing: 0.5px;
            margin-bottom: 35px;

            &:focus {
                outline-width: 0;
            }
            > button {
                width: 100%;
            }
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

export const product1by6ProductStyles: ProductCarouselProductStyles = {
    productImageWrapperStyles: {
        css: css`
            width: 100%;
            height: 200px;
            display: flex;
            justify-content: center;
            align-items: flex-start;
            overflow: hidden;
            max-width: 100%;
        `,
    },
    productInfoWrapperStyles: {
        css: css`
            width: 100%;
            margin-top: 8px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            color: "text.main",
            typographyProps: {
                size: 14,
                weight: "normal",
                lineHeight: "18px",
            },
            css: css`
                margin-bottom: 5px;
                height: 54px;
                display: flex;
                align-items: flex-start;
                min-height: 54px;
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
            css: css`
                margin-bottom: 5px;
                height: 54px;
                display: flex;
                align-items: flex-start;
                min-height: 54px;
            `,
        },
    },
    productPriceWrapper: {
        css: css`
            margin-bottom: 0;
            * {
                font-size: 24px;
                line-height: 29px;
                font-weight: normal;
                color: ${getColor("text.main")};
            }
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    view,
    settingsCollection,
    hideAvailability,
    showElipsesOn,
    customActions,
    removeProductIdToCompare,
    customActionsTemplate,
    showBadges,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    recommendationId,
    sendProductRecommendationTracking,
}: Props) => {
    // sorting always is important when using state in FC, moving this here to avoid infinite rendering
    if (!product || !productInfo) {
        return null;
    }
    const productStyle =
        view === "carousel"
            ? productCarouselProductStyles
            : view === "2*3"
            ? product2by3ProductStyles
            : product1by6ProductStyles;
    const [styles] = useState(() => mergeToNew(productStyle, extendedStyles));
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };
    const [isTouch, setIsTouch] = React.useState(false);

    useEffect(() => {
        // detecting if device is in touch mode to add the dragable ability for product carousel
        if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
            setIsTouch(true);
        }
    }, []);

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const badges = (showBadges && product.properties["badges"] && JSON.parse(product.properties["badges"])) || [];
    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.topWrapper} className={isTouch ? "" : "no-swiping-elements"}>
                    <StyledWrapper {...styles.productImageInfoWrapperStyles}>
                        {view === "carousel" && badges?.length > 0 && (
                            <StyledWrapper
                                {...styles.badgesContainerStyle}
                                className={customActionsTemplate ? "hascustomActionsTemplate" : ""}
                            >
                                {badges.map(
                                    (
                                        badge: { Image: string | undefined; DisplayName: string; Description: string },
                                        index: any,
                                    ) =>
                                        badge.Image && (
                                            <StyledWrapper
                                                css={css`
                                                    margin-left: 5px;
                                                `}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`badge${index}`}
                                            >
                                                <XPopup
                                                    type="tooltip"
                                                    trigger={
                                                        <StyledWrapper>
                                                            <LazyImage src={badge.Image} />
                                                        </StyledWrapper>
                                                    }
                                                    position="bottom center"
                                                    fixed={false}
                                                >
                                                    <StyledWrapper className="popup-content-wrapper">
                                                        {/* eslint-disable react/no-danger */}
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html:
                                                                    badge.DisplayName === "PROP_65_CANCER"
                                                                        ? product.properties.prop65Text ||
                                                                          (siteMessage(
                                                                              "Custom_Prop65DefaultText",
                                                                          ) as string)
                                                                        : badge.Description,
                                                            }}
                                                        ></div>
                                                    </StyledWrapper>
                                                </XPopup>
                                            </StyledWrapper>
                                        ),
                                )}
                            </StyledWrapper>
                        )}
                        <StyledWrapper
                            {...styles.productImageWrapperStyles}
                            onClick={() => (window as any)._MpProductCarouselClick(productContext)}
                        >
                            {/* for image */}
                            {showImage && !showOverlayBadge && (
                                <ProductImage
                                    product={productContext}
                                    onClick={() => trackProductRecommendation(true)}
                                    extendedStyles={styles.productImageStyles}
                                />
                            )}
                            {showImage && showOverlayBadge && overlayBadge && (
                                <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCarouselBadge">
                                    <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                                    <StyledWrapper {...overlayBadgeWrapperStyles}>
                                        <Badge
                                            type={overlayBadge.badgeType}
                                            value={
                                                overlayBadge.badgeType === "Image"
                                                    ? overlayBadge.largeImageBadgePath
                                                    : overlayBadge.displayText
                                            }
                                            badgeTextColor={overlayBadge.textColorHexCode}
                                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                            imageAltText={
                                                overlayBadge.imageAltText
                                                    ? overlayBadge.imageAltText
                                                    : overlayBadge.name
                                            }
                                            textBadgeCss={styles.badgeText}
                                            imageBadgeProps={styles.badgeImageProps}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.productInfoWrapperStyles}>
                            <StyledWrapper onClick={() => (window as any)._MpProductCarouselClick(productContext)}>
                                <ProductDescription
                                    product={productContext}
                                    onClick={() => trackProductRecommendation(true)}
                                    extendedStyles={styles.productDescriptionStyles}
                                    showElipses={true}
                                    showElipsesOn={showElipsesOn || "3"}
                                />
                            </StyledWrapper>
                            {showTextBadges && textBadges && (
                                <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                            )}
                            {showImageBadges && imageBadges && (
                                <ProductImageBadges
                                    badgeList={imageBadges}
                                    maxBadges={maximumNumberOfImageBadges ?? 4}
                                />
                            )}
                            {showPartNumbers && view === "carousel" && (
                                <StyledWrapper {...styles.partNumbersStyles}>
                                    <span className="product-attr-label">
                                        {product.customerProductNumber
                                            ? siteMessage("Custom_Your_Part_#", "Your Part #:")
                                            : product?.properties?.specNumber?.length
                                            ? siteMessage("Custom_Spec_#", "Spec #:")
                                            : product.manufacturerItem
                                            ? siteMessage("Custom_MFR_#", "MFR #:")
                                            : siteMessage("Custom_Part_#", "EIS Part #:")}
                                    </span>
                                    &nbsp;
                                    <span className="product-attr-value">
                                        {product.customerProductNumber
                                            ? product.customerProductNumber
                                            : product?.properties?.specNumber?.length
                                            ? product.properties.specNumber
                                            : product.manufacturerItem
                                            ? product.manufacturerItem
                                            : product.productNumber}
                                    </span>
                                    <PopupContextProvider>
                                        <XPopup
                                            type="tooltip"
                                            trigger={
                                                <StyledWrapper {...styles.moreNumbersMenu}>
                                                    <Icon src={MiniMenu} color="secondary" />
                                                </StyledWrapper>
                                            }
                                            position="top center"
                                            fixed={false}
                                            adjustOnMobile={false}
                                            css={css`
                                                &:before {
                                                    border-color: transparent transparent ${getColor("common.border")};
                                                }
                                                &:after {
                                                    border-color: transparent transparent
                                                        ${getColor("common.background")};
                                                }
                                                > div {
                                                    padding: 18px;
                                                    border: 1px solid ${getColor("common.ui4")};
                                                    width: 100%;
                                                }
                                                .close-popup-icon {
                                                    cursor: pointer;
                                                    position: absolute;
                                                    top: 10px;
                                                    right: 10px;
                                                    color: ${getColor("common.border")};
                                                }
                                            `}
                                        >
                                            {({ handleCloseClick }: any) => (
                                                <StyledWrapper className="popup-content-wrapper">
                                                    <Icon
                                                        src={Close}
                                                        onClick={handleCloseClick}
                                                        size={10}
                                                        className="close-popup-icon"
                                                    />
                                                    <ProductPartNumbers
                                                        productNumber={product.productNumber}
                                                        customerProductNumber={product.customerProductNumber}
                                                        manufacturerItem={product.manufacturerItem}
                                                        specNumber={product.properties.specNumber || ""}
                                                        extendedStyles={styles.partNumbersStylesComponent}
                                                        showLabel={true}
                                                    />
                                                </StyledWrapper>
                                            )}
                                        </XPopup>
                                    </PopupContextProvider>
                                </StyledWrapper>
                            )}
                            {showBrand && view === "carousel" && (
                                <StyledWrapper
                                    css={css`
                                        margin-bottom: 16px;
                                        min-height: 24px;
                                    `}
                                >
                                    {product.brand?.name && (
                                        <>
                                            <span className="product-attr-label">{translate("Brand:")}</span>&nbsp;
                                            <span className="product-attr-value">{product.brand?.name}</span>
                                        </>
                                    )}
                                </StyledWrapper>
                            )}
                            {showPrice && !product.isVariantParent && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductPrice showLabel={false} product={productContext} />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    {view !== "1*6" && (
                        <StyledWrapper>
                            {/* availability */}
                            {!hideAvailability && settingsCollection.productSettings.showInventoryAvailability && (
                                <StyledWrapper {...styles.productAvailabilityWrapper}>
                                    <ProductContextAvailability />
                                </StyledWrapper>
                            )}
                            {/* actions */}
                            {(showAddToCart || showAddToList) && (
                                <>
                                    {!product.isVariantParent ? (
                                        <>
                                            <StyledWrapper {...styles.addToCartContainer}>
                                                {(product?.unitOfMeasures as any)?.length > 1 && showAddToCart && (
                                                    <StyledWrapper {...styles.uomGridItemStyle}>
                                                        <ProductUnitOfMeasureSelect
                                                            labelOverride=""
                                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                                            id={`${carouselId}-uom`}
                                                        />
                                                    </StyledWrapper>
                                                )}
                                                {showAddToCart && (
                                                    <StyledWrapper {...styles.qtyGridItem}>
                                                        <VisuallyHidden
                                                            as="label"
                                                            htmlFor={`${carouselId}-qty`}
                                                            id={`${carouselId}-qty-label`}
                                                        >
                                                            {translate("QTY_quantity")}
                                                        </VisuallyHidden>
                                                        <ProductQuantityOrdered
                                                            labelOverride=""
                                                            extendedStyles={styles.productQuantityOrderedStyles}
                                                            id={`${carouselId}-qty`}
                                                        />
                                                    </StyledWrapper>
                                                )}
                                                <StyledWrapper
                                                    className={
                                                        (product?.unitOfMeasures as any)?.length > 1
                                                            ? "has-multiple-uom"
                                                            : "no-multiple-uom"
                                                    }
                                                    {...styles.addToCartButtonGridItem}
                                                >
                                                    <StyledWrapper
                                                        css={css`
                                                            min-height: 32px;
                                                        `}
                                                    >
                                                        {showAddToCart && (
                                                            <ProductAddToCartButton
                                                                onAddToCartClick={() => trackProductRecommendation(false)}
                                                                data-test-selector="addToCartBtn"
                                                                extendedStyles={styles.productAddToCartButtonStyles}
                                                                {...({
                                                                    sizeVariant:
                                                                        view === "carousel" ? "small" : "large",
                                                                } as any)}
                                                            />
                                                        )}
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            {showAddToList && product.canAddToWishlist && (
                                                <StyledWrapper
                                                    css={css`
                                                        display: flex;
                                                        justify-content: center;
                                                    `}
                                                >
                                                    <ProductAddToListLink
                                                        data-test-selector="addToListLink"
                                                        extendedStyles={styles.productAddToListLinkStyles}
                                                        labelOverride={translate("Shopping List")}
                                                        showIcon={true}
                                                    />
                                                </StyledWrapper>
                                            )}
                                        </>
                                    ) : (
                                        <StyledWrapper {...styles.addToCartContainer}>
                                            {showAddToCart && (
                                                <Clickable
                                                    href={productInfo.productDetailPath}
                                                    {...styles.chooseOptionsClickableStyles}
                                                    onClick={() =>
                                                        (window as any)._MpProductCarouselClick(productContext)
                                                    }
                                                >
                                                    <Button variant="primary">
                                                        {translate("Choose from {0} Options").replace(
                                                            "{0}",
                                                            product.properties["styledChildrenCount"],
                                                        )}
                                                    </Button>
                                                </Clickable>
                                            )}
                                        </StyledWrapper>
                                    )}
                                </>
                            )}
                            {customActions && customActionsTemplate && (
                                <StyledWrapper {...styles.customActionStyle}>
                                    {customActionsTemplate(product)}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </ProductContext.Provider>
        </>
    );
};

ProductCarouselProduct.defaultProps = {
    showBadges: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
